import {
  type Column,
  type ColumnDef,
  type Row,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '../../ui/pagination';
import { Skeleton } from '../../ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { cn } from '../../ui/utils';
import { GroupHeader } from './GroupHeader';
import { tt } from './messages';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- the augmented interface's parameters are the library's
  interface ColumnMeta<TData, TValue> {
    numeric?: boolean;
    /** Dropped below 768 rather than joining the horizontal scroll; a column with no
     *  priority always renders, same as before this existed. */
    priority?: 'low';
    /** A CSS width (e.g. `'96px'`), applied to both the header and body cells. Opt-in and
     *  additive: a column with no `width` renders exactly as before this existed. Its one
     *  purpose is two independently-measured `DataTable`s (e.g. two cards of the same
     *  `columns`) landing on the same per-column geometry, which `table-layout: auto` cannot
     *  otherwise promise -- react-table's own `size` is not used for this because its default
     *  (150) is indistinguishable from an explicit one, which would size every column of every
     *  `DataTable` in the app that never asked for it. */
    width?: string;
  }
}

function isNumeric<T>(column: Column<T, unknown>): boolean {
  return column.columnDef.meta?.numeric === true;
}

function isLowPriority<T>(column: Column<T, unknown>): boolean {
  return column.columnDef.meta?.priority === 'low';
}

function widthOf<T>(column: Column<T, unknown>): string | undefined {
  return column.columnDef.meta?.width;
}

function ariaSort(sorted: false | 'asc' | 'desc'): 'ascending' | 'descending' | undefined {
  if (sorted === 'asc') return 'ascending';
  if (sorted === 'desc') return 'descending';
  return undefined;
}

export function DataTable<T>(props: {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  /** Renders `pageSize` skeleton rows and an sr-only status; `data` is ignored while true. */
  loading?: boolean;
  /** Rendered in place of the body when `data` is empty and not loading, with no wrapper of
   *  its own: pass an `EmptyState` (`src/chrome/state`) for its own centered, muted container.
   *  Required: an empty table with no sentence is a bug, and the sentence is the screen's, not
   *  the kit's. */
  emptyState: ReactNode;
  /** Client pagination; omit for none. */
  pageSize?: number;
  /** Controlled sorting; omit for uncontrolled with `initialSorting`. */
  sorting?: SortingState;
  onSortingChange?: (next: SortingState) => void;
  initialSorting?: SortingState;
  getRowId?: (row: T) => string;
  /** `<caption>` text, visually hidden; screen readers announce it. */
  caption: string;
  /** Rendered inside the table's own bordered box, above the column
   *  header row -- a filter bar or period reading that belongs beside the
   *  table rather than in a separate card above it. */
  header?: ReactNode;
  /** When given, the row's first cell wraps its content in a real `<a>` that
   *  overlays the whole row (`after:absolute after:inset-0`); the row itself
   *  carries no role, and keyboard/screen readers reach the anchor, not the row. */
  rowHref?: (row: T) => string;
  /** Inserts a header row where `key` changes; rows stay in the order `data`
   *  arrived in and are never re-sorted to consolidate a key, because a table
   *  that re-sorts its own rows disagrees with the pagination the server
   *  did. Column sorting is refused while grouped, for the same reason. */
  groupBy?: {
    key: (row: T) => string;
    header: (key: string, rows: readonly T[]) => ReactNode;
    tone?: (key: string) => 'active' | 'neutral';
  };
}): ReactNode {
  const [internalSorting, setInternalSorting] = useState<SortingState>(props.initialSorting ?? []);
  const sorting = props.groupBy ? [] : (props.sorting ?? internalSorting);
  const table = useReactTable({
    data: props.loading ? [] : props.data,
    columns: props.columns,
    state: { sorting },
    onSortingChange: (updater) => {
      if (props.groupBy) return;
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setInternalSorting(next);
      props.onSortingChange?.(next);
    },
    getCoreRowModel: getCoreRowModel(),
    ...(props.groupBy ? {} : { getSortedRowModel: getSortedRowModel() }),
    ...(props.pageSize !== undefined
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          initialState: { pagination: { pageSize: props.pageSize } },
        }
      : {}),
    ...(props.getRowId !== undefined ? { getRowId: props.getRowId } : {}),
  });

  const rows = table.getRowModel().rows;
  const pageCount = table.getPageCount();
  const skeletonRows = props.pageSize ?? 5;
  // Fixed only once a column actually asks for it -- every other `DataTable` in the app sets
  // no `meta.width` and keeps today's content-sized `auto` layout unchanged.
  const hasExplicitWidths = props.columns.some((column) => column.meta?.width !== undefined);

  function renderDataRow(row: Row<T>): ReactNode {
    const href = props.rowHref?.(row.original);
    return (
      <TableRow key={row.id} className={cn(href !== undefined && 'cursor-pointer')}>
        {row.getVisibleCells().map((cell, index) => {
          const numeric = isNumeric(cell.column);
          const width = widthOf(cell.column);
          const content = flexRender(cell.column.columnDef.cell, cell.getContext());
          return (
            <TableCell
              key={cell.id}
              {...(numeric ? { 'data-numeric': true } : {})}
              style={width !== undefined ? { width } : undefined}
              className={cn(
                numeric && 'text-end',
                isLowPriority(cell.column) && 'hidden md:table-cell',
              )}
            >
              {href !== undefined && index === 0 ? (
                <Link
                  to={href}
                  className="after:absolute after:inset-0 after:rounded-sm focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-inset"
                >
                  {content}
                </Link>
              ) : (
                content
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  function renderBody(): ReactNode {
    if (rows.length === 0) return null;
    const groupBy = props.groupBy;
    if (!groupBy) return rows.map(renderDataRow);

    const nodes: ReactNode[] = [];
    let lastKey: string | undefined;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;
      const key = groupBy.key(row.original);
      if (key !== lastKey) {
        let end = i;
        const groupRows: T[] = [];
        while (end < rows.length && groupBy.key(rows[end]!.original) === key) {
          groupRows.push(rows[end]!.original);
          end++;
        }
        nodes.push(
          <GroupHeader key={`group-${i}`} colSpan={props.columns.length} tone={groupBy.tone?.(key)}>
            {groupBy.header(key, groupRows)}
          </GroupHeader>,
        );
        lastKey = key;
      }
      nodes.push(renderDataRow(row));
    }
    return nodes;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto rounded-lg border">
        {props.header !== undefined && (
          <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">{props.header}</div>
        )}
        <Table
          label={props.caption}
          style={hasExplicitWidths ? { tableLayout: 'fixed' } : undefined}
        >
          <TableCaption className="sr-only">{props.caption}</TableCaption>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => {
                  const width = widthOf(header.column);
                  return (
                    <TableHead
                      key={header.id}
                      aria-sort={ariaSort(header.column.getIsSorted())}
                      style={width !== undefined ? { width } : undefined}
                      className={cn(
                        isNumeric(header.column) && 'text-end',
                        isLowPriority(header.column) && 'hidden md:table-cell',
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {props.loading
              ? Array.from({ length: skeletonRows }, (_, index) => (
                  <TableRow key={index}>
                    {props.columns.map((column, columnIndex) => (
                      <TableCell
                        key={columnIndex}
                        style={
                          column.meta?.width !== undefined
                            ? { width: column.meta.width }
                            : undefined
                        }
                        className={cn(column.meta?.priority === 'low' && 'hidden md:table-cell')}
                      >
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : renderBody()}
          </TableBody>
        </Table>
        {!props.loading && rows.length === 0 && props.emptyState}
      </div>
      {props.loading && (
        <p role="status" className="sr-only">
          {tt('loading')}
        </p>
      )}
      {props.pageSize !== undefined && !props.loading && pageCount > 1 && (
        <Pagination aria-label={tt('pagination.label')} className="justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                aria-label={tt('pagination.previous')}
                aria-disabled={!table.getCanPreviousPage()}
                tabIndex={!table.getCanPreviousPage() ? -1 : undefined}
                className={cn(!table.getCanPreviousPage() && 'pointer-events-none opacity-50')}
                onClick={(event) => {
                  event.preventDefault();
                  table.previousPage();
                }}
              >
                {tt('pagination.previous')}
              </PaginationPrevious>
            </PaginationItem>
            <PaginationItem>
              <span className="text-caption text-muted-foreground px-2" data-numeric>
                {tt('pagination.page', {
                  page: table.getState().pagination.pageIndex + 1,
                  pages: pageCount,
                })}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                aria-label={tt('pagination.next')}
                aria-disabled={!table.getCanNextPage()}
                tabIndex={!table.getCanNextPage() ? -1 : undefined}
                className={cn(!table.getCanNextPage() && 'pointer-events-none opacity-50')}
                onClick={(event) => {
                  event.preventDefault();
                  table.nextPage();
                }}
              >
                {tt('pagination.next')}
              </PaginationNext>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
